import { Close, Token, tokenize } from './tokenizer'

export const BlockType = {
  ']': 'scoped',
  ')': 'array',
  '}': 'proc',
} as const

export type BlockType = typeof BlockType
export type BlockClose = keyof BlockType

export type CstNodeType =
  | 'array'
  | 'id'
  | 'label'
  | 'native'
  | 'number'
  | 'op'
  | 'proc'
  | 'prog'
  | 'scoped'

export interface CstNode<T = CstNodeType> extends Token<T> {
  tokens: CstNode<any>[]
  stack: CstNode<any>[]
  owner?: CstNode<any>
  parent?: CstNode<any>[]
}

export type CstVisitors<T> = { [K in CstNodeType]: (item: CstNode<K>, v: T, next?: CstNode<CstNodeType> | null) => void }

function isCstToken(token: Token) {
  switch (token.type) {
    case 'any':
    case 'comment': return false
  }
  return true
}

function flattenNodes(items: CstNode[]) {
  let result: CstNode[] = []
  for (const item of items) {
    if ('stack' in item) {
      result = result.concat(flattenNodes(item.stack))
    }
  }
  result = result.concat(items)
  return result
}

export function bounds(tokens: CstNode[]) {
  let line: number = Infinity
  let col: number = Infinity
  let right: number = 0
  let index: number = Infinity
  let bottom: number = 0

  let b: { line: number, col: number, right: number, index: number }


  for (const n of tokens) {
    if ('stack' in n && n.stack.length) b = bounds(n.stack)
    else b = n
    if (b.index < index) index = b.index
    if (b.line < line) line = b.line
    if (b.line > bottom) bottom = b.line
    if (b.col < col && b.line === line) col = b.col
    if (b.right > right) right = b.right
  }

  return { line, col, right, bottom, index }
}

export function parse(inputTokens: Token[]): CstNode<'prog'> {
  const tokens = inputTokens.filter(isCstToken) as CstNode[]
  const end = tokens.length
  let i = 0
  let token!: CstNode
  let peek: CstNode | undefined = tokens[i]

  function next() {
    token = peek!
    peek = tokens[++i]
  }

  function block(open: CstNode, close: BlockClose): CstNode<BlockType[BlockClose]> {
    const stack: CstNode[] = []
    while (i < end) {
      next()
      switch (token.text) {
        case close: {
          const tokens = [open, token].concat(flattenNodes(stack))
          const node: CstNode<BlockType[BlockClose]> = Object.assign(
            { type: BlockType[close], text: close, tokens, stack, meta: {} },
            bounds(tokens)
          )
          tokens.forEach(t => t.owner = node)
          return node
        }
      }
      stack.push(Object.assign(process(stack), { parent: stack }))
    }
    throw new SyntaxError(`Expected "${close}".`, { cause: { nodes: [open] } })
  }

  function label(): CstNode<'label'> {
    const name = Object.create(token) as CstNode<'label'>
    next() // ':'
    // next()
    // const value = process()
    // if (value.type === 'op' && value.text !== '=') {
    //   throw new SyntaxError(`Unexpected "${value.text}".`, { cause: { nodes: [value] } })
    // // }
    // const tokens = [name] //, ...('tokens' in value ? value.tokens : [value])]
    // const b = bounds(tokens)
    // const node: CstNode<'label'> = { type: 'label', text: name.text, tokens, stack: [name], ...b, meta: {} }

    name.type = 'label'
    return name //Object.assign(name, { ...name, type: 'label' }
  }

  function process(stack: CstNode[]): CstNode {
    switch (token.text) {
      case '(':
      case '{':
      case '[': return block(token, Close[token.text])

      case '+=': {
        const add = { ...token, text: '+' }
        const eq = { ...token, text: '=' }
        const last = stack.at(-1)!
        stack.push(add)
        stack.push(last)
        return eq
      }
    }

    switch (token.type) {
      case 'id':
        if (peek?.text === ':') return label()
    }

    return token
  }

  const stack: CstNode[] = []
  while (i < end) {
    next()
    stack.push(Object.assign(process(stack), { parent: stack }))
  }

  return { type: 'prog', text: 'prog', stack, tokens, line: 0, col: 0, right: 0, bottom: 0, index: 0, meta: {} }
}

export function test() {
  // @env browser
  describe('parse', () => {
    const passing: [string, any][] = [
      ['[sin 200 [20 sin]+]', {
        stack: [
          {
            stack: [
              { text: 'sin' },
              { text: '200' },
              { stack: [{ text: '20' }, { text: 'sin' }] },
              { text: '+' },
            ]
          }
        ]
      }],

      ['[lpf 1k2 q:2]', {
        stack: [
          {
            stack: [
              { type: 'id', text: 'lpf' },
              { type: 'number', text: '1k2' },
              { type: 'label', stack: [{ text: 'q' }, { text: '2' }] },
            ]
          }
        ]
      }],
    ]

    passing.forEach(([code, expected]) => {
      it(code, () => {
        const cst = parse([...tokenize(code)])
        expect(cst).toMatchObject(expected)
      })
    })
  })
}
